import { useCallback, useEffect, useRef } from 'react';
import { Layer, LayerProps, MapboxGeoJSONFeature, MapLayerMouseEvent, Source, useMap } from 'react-map-gl';

import {
  CELLS_FILL_LAYER_ID,
  CELLS_OUTLINE_LAYER_ID,
  INTERACTIVE_LAYERS,
  PLOT_CELLS,
} from '@/pages/shared/plots/pages/constants';
import {
  CellFeature,
  ControlsFormFieldValues,
  InteractiveMapFormFields,
  useInteractiveMapControlsContext,
} from '@/pages/shared/plots/pages/hooks/useControlsForm';
import { usePlotCells } from '@/pages/shared/plots/pages/hooks/usePlotCells';

export const PlotCells = () => {
  const mapRef = useMap();

  const plotCells = usePlotCells();

  const controlsForm = useInteractiveMapControlsContext();

  const { watch } = controlsForm;

  const previousCellRef = useRef<ControlsFormFieldValues[InteractiveMapFormFields.Cell]>(null);

  useEffect(
    // eslint-disable-next-line prefer-arrow-callback
    function handleCellChange() {
      const map = mapRef.current;

      if (!map) {
        return undefined;
      }

      const { unsubscribe } = watch(({ cell }, { name }) => {
        if (name !== InteractiveMapFormFields.Cell) {
          return null;
        }

        map.setFeatureState(
          { source: PLOT_CELLS, id: previousCellRef.current?.properties?.cell_id ?? '' },
          { active: false },
        );

        if (cell) {
          map.setFeatureState({ source: PLOT_CELLS, id: cell.properties?.cell_id }, { active: true });
          previousCellRef.current = cell as CellFeature;
        }

        return null;
      });

      return unsubscribe;
    },
    [watch, mapRef],
  );

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const map = event.target;

      const featureUnderMousePointer = map.queryRenderedFeatures(event.point, {
        layers: INTERACTIVE_LAYERS,
      })?.[0] as (CellFeature & Omit<MapboxGeoJSONFeature, keyof CellFeature>) | undefined;

      if (!featureUnderMousePointer) {
        return null;
      }

      if (!INTERACTIVE_LAYERS.includes(featureUnderMousePointer.layer.id)) {
        return null;
      }

      const featureId = featureUnderMousePointer?.properties?.cell_id;

      if (!featureId) {
        return null;
      }

      controlsForm.setValue(InteractiveMapFormFields.Cell, featureUnderMousePointer);

      return null;
    },
    [controlsForm],
  );

  const hoveredPolygonIdRef = useRef<number | null>(null);

  const handleMapMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const map = event.target;

    const featureUnderMousePointer = map.queryRenderedFeatures(event.point, {
      layers: INTERACTIVE_LAYERS,
    })?.[0];
    const featureId = featureUnderMousePointer?.properties?.cell_id as number | undefined;

    if (!featureId) {
      map.getCanvas().style.cursor = 'initial';

      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState({ source: PLOT_CELLS, id: hoveredPolygonIdRef.current as number }, { hover: false });
        hoveredPolygonIdRef.current = null;
      }
    } else {
      map.getCanvas().style.cursor = 'pointer';

      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState({ source: PLOT_CELLS, id: hoveredPolygonIdRef.current as number }, { hover: false });
      }

      hoveredPolygonIdRef.current = featureId;
      map.setFeatureState({ source: PLOT_CELLS, id: hoveredPolygonIdRef.current as number }, { hover: true });
    }
  }, []);

  useEffect(
    // eslint-disable-next-line prefer-arrow-callback
    function addMouseHandlers() {
      const map = mapRef.current;

      if (!map) {
        return undefined;
      }

      const layers = [CELLS_OUTLINE_LAYER_ID, CELLS_FILL_LAYER_ID];

      map.on('click', layers, handleMapClick);
      map.on('mousemove', layers, handleMapMouseMove);

      return () => {
        map.off('click', layers, handleMapClick);
        map.off('mousemove', layers, handleMapMouseMove);
      };
    },
    [mapRef, handleMapMouseMove, handleMapClick],
  );

  return (
    <Source id={PLOT_CELLS} type='geojson' data={plotCells} promoteId='cell_id'>
      <Layer {...cellsOutlineLayer} />
      <Layer {...cellsFillLayer} />
    </Source>
  );
};

const cellsOutlineLayer: LayerProps = {
  id: CELLS_OUTLINE_LAYER_ID,
  type: 'line',
  source: PLOT_CELLS,
  paint: {
    'line-width': 1.5,
    'line-opacity': 0.5,
    'line-color': 'rgb(255, 255, 255)',
  },
};

const cellsFillLayer: LayerProps = {
  id: CELLS_FILL_LAYER_ID,
  type: 'fill',
  interactive: true,
  source: PLOT_CELLS,
  paint: {
    'fill-color': '#F3EDD3',
    'fill-opacity': [
      'case',
      ['boolean', ['feature-state', 'active'], false],
      0.8,
      ['boolean', ['feature-state', 'hover'], false],
      0.6,
      0.0,
    ],
  },
};
